<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { toast } from 'dashboard/composables/useToast';
import ContactsAPI from 'dashboard/api/contacts';

const props = defineProps({
  show: { type: Boolean, default: false },
  segmentId: { type: [Number, String], default: null },
  segmentName: { type: String, default: '' },
});

const emit = defineEmits(['close', 'imported']);

// Importar é carga de dados, não acontecimento de negócio — e é onde um erro de
// planilha vira mensagem para gente errada. Por isso nasce DESMARCADO: quem
// quiser a régua rodando precisa dizer que quer.
const avisarFluxos = ref(false);

const { t } = useI18n();

const currentStep = ref(1);
const file = ref(null);
const fileName = ref('');
const isDragging = ref(false);
const isPreviewLoading = ref(false);
const isImporting = ref(false);

const store = useStore();
const segments = useMapGetter('segments/getSegments');

// Aberto pela tela de Contatos não há lista escolhida: o assistente começa
// perguntando o destino. Aberto de dentro de uma lista, esse passo não existe.
const escolheLista = computed(() => !props.segmentId);
const listaEscolhidaId = ref(null);
const criandoLista = ref(false);
const nomeDaListaNova = ref('');
const isCreatingList = ref(false);

// Só LISTA recebe importação — segmento com regra é recalculado e desfaria.
const listasDisponiveis = computed(() =>
  (segments.value || []).filter(s => s.segment_type === 'list')
);

const segmentoDestinoId = computed(
  () => props.segmentId || listaEscolhidaId.value
);

const nomeDoDestino = computed(() => {
  if (props.segmentName) return props.segmentName;
  const lista = listasDisponiveis.value.find(
    l => l.id === listaEscolhidaId.value
  );
  return lista?.name || '';
});

const podeSeguirDaLista = computed(
  () =>
    !!listaEscolhidaId.value ||
    (criandoLista.value && !!nomeDaListaNova.value.trim())
);

const criarListaSeNecessario = async () => {
  if (!criandoLista.value) return true;

  isCreatingList.value = true;
  try {
    const response = await store.dispatch('segments/create', {
      name: nomeDaListaNova.value.trim(),
      description: '',
      query: { payload: [] },
      segment_type: 'list',
      active: true,
    });
    const nova = response?.data?.payload || response?.data;
    if (!nova?.id) throw new Error('sem id');
    listaEscolhidaId.value = nova.id;
    criandoLista.value = false;
    return true;
  } catch {
    toast.error(t('SEGMENT_IMPORT.LIST.CREATE_ERROR'));
    return false;
  } finally {
    isCreatingList.value = false;
  }
};

const seguirDaLista = async () => {
  if (!podeSeguirDaLista.value) return;
  if (!(await criarListaSeNecessario())) return;

  currentStep.value = 1;
};

// Preview data from backend
const previewHeaders = ref([]);
const previewRows = ref([]);
const suggestedMapping = ref({});
const availableFields = ref([]);
const customFields = ref([]);
const totalRows = ref(0);

// User-defined mapping
const fieldMapping = ref({});

// Import result
const importResult = ref(null);

// Nomes iguais aos da ficha do contato no CRM.
const fieldLabels = computed(() => ({
  name: t('SEGMENT_IMPORT.FIELDS.NAME'),
  last_name: t('SEGMENT_IMPORT.FIELDS.LAST_NAME'),
  email: t('SEGMENT_IMPORT.FIELDS.EMAIL'),
  phone_number: t('SEGMENT_IMPORT.FIELDS.PHONE'),
  identifier: t('SEGMENT_IMPORT.FIELDS.IDENTIFIER'),
  company: t('SEGMENT_IMPORT.FIELDS.COMPANY'),
  city: t('SEGMENT_IMPORT.FIELDS.CITY'),
  country_code: t('SEGMENT_IMPORT.FIELDS.COUNTRY'),
}));

const stepLabels = computed(() => {
  const passos = [
    t('SEGMENT_IMPORT.STEPS.UPLOAD'),
    t('SEGMENT_IMPORT.STEPS.MAPPING'),
    t('SEGMENT_IMPORT.STEPS.CONFIRM'),
    t('SEGMENT_IMPORT.STEPS.RESULT'),
  ];
  return escolheLista.value
    ? [t('SEGMENT_IMPORT.STEPS.LIST'), ...passos]
    : passos;
});

// Com o passo da lista, o "Upload" é o 2º da régua: o indicador soma 1.
const passoNaRegua = computed(() =>
  escolheLista.value ? currentStep.value + 1 : currentStep.value
);

const mappedFieldCount = computed(() => {
  return Object.values(fieldMapping.value).filter(v => v && v !== 'ignore')
    .length;
});

const hasEmailOrPhone = computed(() => {
  const mapped = Object.values(fieldMapping.value);
  return mapped.includes('email') || mapped.includes('phone_number');
});

// --- File handling ---
const fetchPreview = async () => {
  if (!file.value) return;
  isPreviewLoading.value = true;
  try {
    const response = await ContactsAPI.importPreview(file.value);
    const data = response.data;
    previewHeaders.value = data.headers || [];
    previewRows.value = data.preview || [];
    suggestedMapping.value = data.suggested_mapping || {};
    availableFields.value = data.available_fields || [];
    customFields.value = data.custom_fields || [];
    totalRows.value = data.total_rows || 0;

    // Initialize mapping with suggestions
    const mapping = {};
    previewHeaders.value.forEach(header => {
      mapping[header] = suggestedMapping.value[header] || '';
    });
    fieldMapping.value = mapping;

    currentStep.value = 2;
  } catch (error) {
    const msg =
      error.response?.data?.error || t('SEGMENT_IMPORT.UPLOAD.READ_ERROR');
    toast.error(msg);
  } finally {
    isPreviewLoading.value = false;
  }
};

const handleFile = async f => {
  // .xls (Excel 97-2003) fica de fora: o servidor não tem como abrir, e
  // anunciar o que não funciona é pior do que pedir pra salvar como .xlsx.
  const ext = f.name.split('.').pop().toLowerCase();
  if (!['xlsx', 'csv'].includes(ext)) {
    toast.error(t('SEGMENT_IMPORT.UPLOAD.UNSUPPORTED'));
    return;
  }
  file.value = f;
  fileName.value = f.name;
  await fetchPreview();
};

const onFileSelect = event => {
  const f = event.target.files[0];
  if (f) handleFile(f);
};

const onDrop = event => {
  isDragging.value = false;
  const f = event.dataTransfer.files[0];
  if (f) handleFile(f);
};

const downloadTemplate = async () => {
  try {
    const response = await ContactsAPI.getImportTemplate();
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modelo_importacao_contatos.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  } catch {
    toast.error(t('SEGMENT_IMPORT.UPLOAD.TEMPLATE_ERROR'));
  }
};

// Dedup stats
const isChecking = ref(false);
const dedupStats = ref(null);

const goToConfirmation = async () => {
  if (!hasEmailOrPhone.value) {
    toast.warning(t('SEGMENT_IMPORT.MAPPING.NEED_EMAIL_OR_PHONE'));
    return;
  }
  // Run dedup check before showing confirmation
  isChecking.value = true;
  try {
    const response = await ContactsAPI.importCheck(
      file.value,
      fieldMapping.value
    );
    dedupStats.value = response.data;
  } catch {
    // If check fails, show totals only
    dedupStats.value = {
      total: totalRows.value,
      new_contacts: totalRows.value,
      existing_contacts: 0,
    };
  } finally {
    isChecking.value = false;
  }
  currentStep.value = 3;
};

const startImport = async () => {
  if (!file.value || isImporting.value) return;
  isImporting.value = true;
  try {
    await ContactsAPI.importContacts(file.value, {
      segmentId: segmentoDestinoId.value,
      fieldMapping: fieldMapping.value,
      notifyFlows: avisarFluxos.value,
    });
    importResult.value = { success: true, total: totalRows.value };
    currentStep.value = 4;
    emit('imported');
  } catch (error) {
    const msg =
      error.response?.data?.error || t('SEGMENT_IMPORT.RESULT.IMPORT_ERROR');
    importResult.value = { success: false, error: msg };
    currentStep.value = 4;
  } finally {
    isImporting.value = false;
  }
};

const reset = () => {
  currentStep.value = escolheLista.value ? 0 : 1;
  listaEscolhidaId.value = null;
  criandoLista.value = false;
  nomeDaListaNova.value = '';
  file.value = null;
  fileName.value = '';
  previewHeaders.value = [];
  previewRows.value = [];
  fieldMapping.value = {};
  importResult.value = null;
  dedupStats.value = null;
};

const close = () => {
  reset();
  emit('close');
};

watch(
  () => props.show,
  aberto => {
    if (!aberto) return;
    currentStep.value = escolheLista.value ? 0 : 1;
    if (escolheLista.value) store.dispatch('segments/get');
  },
  { immediate: true }
);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="close"
    >
      <div
        class="bg-white dark:bg-n-solid-2 rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col overflow-hidden"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak shrink-0"
        >
          <div>
            <h2 class="text-lg font-semibold text-n-slate-12">
              {{ t('SEGMENT_IMPORT.TITLE') }}
            </h2>
            <p v-if="nomeDoDestino" class="text-xs text-n-slate-10 mt-0.5">
              {{ t('SEGMENT_IMPORT.FOR_SEGMENT', { name: nomeDoDestino }) }}
            </p>
          </div>
          <button
            class="p-1.5 rounded-lg hover:bg-n-alpha-1 transition-colors"
            @click="close"
          >
            <span class="i-lucide-x size-5 text-n-slate-9" />
          </button>
        </div>

        <!-- Steps indicator -->
        <div
          class="flex items-center gap-0 px-6 py-3 border-b border-n-weak shrink-0"
        >
          <template v-for="(label, idx) in stepLabels" :key="idx">
            <div class="flex items-center gap-2">
              <div
                class="flex items-center justify-center size-6 rounded-full text-[11px] font-bold"
                :class="
                  passoNaRegua > idx + 1
                    ? 'bg-green-500 text-white'
                    : passoNaRegua === idx + 1
                      ? 'bg-woot-500 text-white'
                      : 'bg-n-alpha-1 text-n-slate-9'
                "
              >
                <span
                  v-if="passoNaRegua > idx + 1"
                  class="i-lucide-check size-3.5"
                />
                <template v-else>{{ idx + 1 }}</template>
              </div>
              <span
                class="text-xs font-medium"
                :class="
                  passoNaRegua >= idx + 1 ? 'text-n-slate-12' : 'text-n-slate-9'
                "
              >
                {{ label }}
              </span>
            </div>
            <div
              v-if="idx < stepLabels.length - 1"
              class="flex-1 h-px mx-3"
              :class="
                passoNaRegua > idx + 1
                  ? 'bg-green-300 dark:bg-green-600'
                  : 'bg-n-slate-5'
              "
            />
          </template>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto p-6">
          <!-- PASSO 0: para qual lista -->
          <div v-if="currentStep === 0">
            <h3 class="text-sm font-semibold text-n-slate-12 mb-1">
              {{ t('SEGMENT_IMPORT.LIST.TITLE') }}
            </h3>
            <p class="text-xs text-n-slate-11 mb-4">
              {{ t('SEGMENT_IMPORT.LIST.SUBTITLE') }}
            </p>

            <template v-if="!criandoLista">
              <label class="flex flex-col gap-1 mb-3">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ t('SEGMENT_IMPORT.LIST.CHOOSE') }}
                </span>
                <select
                  v-model="listaEscolhidaId"
                  class="reset-base w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-background text-n-slate-12"
                >
                  <option :value="null">
                    {{ t('SEGMENT_IMPORT.LIST.PLACEHOLDER') }}
                  </option>
                  <option
                    v-for="lista in listasDisponiveis"
                    :key="lista.id"
                    :value="lista.id"
                  >
                    {{ lista.name }}
                  </option>
                </select>
              </label>
              <button
                class="text-xs text-n-brand hover:underline"
                @click="criandoLista = true"
              >
                {{ t('SEGMENT_IMPORT.LIST.CREATE_LINK') }}
              </button>
              <p
                v-if="!listasDisponiveis.length"
                class="mt-3 text-xs text-n-slate-10"
              >
                {{ t('SEGMENT_IMPORT.LIST.EMPTY') }}
              </p>
            </template>

            <template v-else>
              <label class="flex flex-col gap-1 mb-3">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ t('SEGMENT_IMPORT.LIST.NEW_NAME') }}
                </span>
                <input
                  v-model="nomeDaListaNova"
                  type="text"
                  :placeholder="t('SEGMENT_IMPORT.LIST.NEW_PLACEHOLDER')"
                  class="reset-base w-full text-sm border border-n-weak rounded-lg px-3 py-2 bg-n-background text-n-slate-12"
                  @keyup.enter="seguirDaLista"
                />
              </label>
              <button
                class="text-xs text-n-slate-11 hover:text-n-slate-12 hover:underline"
                @click="criandoLista = false"
              >
                {{ t('SEGMENT_IMPORT.LIST.CHOOSE_EXISTING') }}
              </button>
            </template>

            <div class="flex justify-end mt-6">
              <button
                class="px-4 py-2 text-sm font-medium rounded-lg bg-woot-500 text-white disabled:opacity-50"
                :disabled="!podeSeguirDaLista || isCreatingList"
                @click="seguirDaLista"
              >
                {{
                  isCreatingList
                    ? t('SEGMENT_IMPORT.LIST.CREATING')
                    : t('SEGMENT_IMPORT.MAPPING.CONTINUE')
                }}
              </button>
            </div>
          </div>

          <!-- STEP 1: Upload -->
          <div v-if="currentStep === 1">
            <div
              class="border-2 border-dashed rounded-xl p-10 text-center transition-colors"
              :class="
                isDragging
                  ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/10'
                  : 'border-n-weak hover:border-n-strong'
              "
              @dragover.prevent="isDragging = true"
              @dragleave="isDragging = false"
              @drop.prevent="onDrop"
            >
              <span
                :class="
                  isPreviewLoading
                    ? 'i-lucide-loader-2 animate-spin'
                    : 'i-lucide-upload-cloud'
                "
                class="size-12 text-n-slate-8 mx-auto block mb-4"
              />
              <p class="text-sm text-n-slate-11 mb-2">
                <template v-if="isPreviewLoading">
                  {{ t('SEGMENT_IMPORT.UPLOAD.READING') }}
                </template>
                <template v-else-if="fileName">
                  <span class="font-medium text-woot-600">{{ fileName }}</span>
                </template>
                <template v-else>
                  {{ t('SEGMENT_IMPORT.UPLOAD.DRAG') }}
                  <label
                    class="text-woot-500 hover:text-woot-600 cursor-pointer font-medium"
                  >
                    {{ t('SEGMENT_IMPORT.UPLOAD.SELECT') }}
                    <input
                      type="file"
                      accept=".xlsx,.csv"
                      class="hidden"
                      @change="onFileSelect"
                    />
                  </label>
                </template>
              </p>
              <p class="text-xs text-n-slate-9">
                {{ t('SEGMENT_IMPORT.UPLOAD.FORMATS') }}
              </p>
            </div>

            <div class="flex items-center justify-center mt-6">
              <button
                class="flex items-center gap-2 text-sm text-woot-500 hover:text-woot-600 font-medium"
                @click="downloadTemplate"
              >
                <span class="i-lucide-file-spreadsheet size-4" />
                {{ t('SEGMENT_IMPORT.UPLOAD.TEMPLATE') }}
              </button>
            </div>
          </div>

          <!-- STEP 2: Mapping -->
          <div v-if="currentStep === 2">
            <div class="mb-4">
              <h3 class="text-sm font-semibold text-n-slate-12 mb-1">
                {{ t('SEGMENT_IMPORT.MAPPING.TITLE') }}
              </h3>
              <p class="text-xs text-n-slate-10">
                {{ t('SEGMENT_IMPORT.MAPPING.SUBTITLE', { count: totalRows }) }}
              </p>
            </div>

            <!-- Mapping table -->
            <div class="border border-n-weak rounded-lg overflow-hidden">
              <table class="w-full">
                <thead class="bg-n-slate-2">
                  <tr>
                    <th
                      class="text-left text-xs font-medium text-n-slate-10 px-4 py-2.5"
                    >
                      {{ t('SEGMENT_IMPORT.MAPPING.COL_FILE') }}
                    </th>
                    <th
                      class="text-center text-xs text-n-slate-9 px-2 py-2.5 w-8"
                    >
                      <span
                        class="i-lucide-arrow-right size-3.5 inline-block text-n-slate-8"
                      />
                    </th>
                    <th
                      class="text-left text-xs font-medium text-n-slate-10 px-4 py-2.5"
                    >
                      {{ t('SEGMENT_IMPORT.MAPPING.COL_FIELD') }}
                    </th>
                    <th
                      class="text-left text-xs font-medium text-n-slate-10 px-4 py-2.5"
                    >
                      {{ t('SEGMENT_IMPORT.MAPPING.COL_PREVIEW') }}
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak">
                  <tr
                    v-for="(header, idx) in previewHeaders"
                    :key="header"
                    class="hover:bg-n-alpha-1/50"
                  >
                    <td class="px-4 py-2.5">
                      <span class="text-sm font-medium text-n-slate-12">
                        {{ header }}
                      </span>
                    </td>
                    <td class="px-2 py-2.5 text-center">
                      <span
                        class="i-lucide-arrow-right size-3.5 text-n-slate-8"
                      />
                    </td>
                    <td class="px-4 py-2.5">
                      <select
                        v-model="fieldMapping[header]"
                        class="w-full text-sm border border-n-weak rounded-md px-2.5 py-1.5 bg-n-solid-1 text-n-slate-12 cursor-pointer"
                      >
                        <option value="">
                          {{ t('SEGMENT_IMPORT.MAPPING.SELECT_OPTION') }}
                        </option>
                        <optgroup
                          :label="t('SEGMENT_IMPORT.MAPPING.GROUP_STANDARD')"
                        >
                          <option
                            v-for="field in availableFields"
                            :key="field"
                            :value="field"
                          >
                            {{ fieldLabels[field] || field }}
                          </option>
                        </optgroup>
                        <optgroup
                          v-if="customFields.length"
                          :label="t('SEGMENT_IMPORT.MAPPING.GROUP_CUSTOM')"
                        >
                          <option
                            v-for="f in customFields"
                            :key="f.key"
                            :value="f.key"
                          >
                            {{ f.label }}
                          </option>
                        </optgroup>
                        <option value="ignore">
                          {{ t('SEGMENT_IMPORT.MAPPING.IGNORE') }}
                        </option>
                      </select>
                    </td>
                    <td class="px-4 py-2.5">
                      <span
                        class="text-xs text-n-slate-9 truncate block max-w-[160px]"
                      >
                        {{ previewRows[0]?.[idx] || '--' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div
              v-if="!hasEmailOrPhone"
              class="flex items-center gap-2 mt-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400"
            >
              <span class="i-lucide-alert-triangle size-4 shrink-0" />
              <span class="text-xs">
                {{ t('SEGMENT_IMPORT.MAPPING.NEED_EMAIL_OR_PHONE') }}
              </span>
            </div>
          </div>

          <!-- STEP 3: Confirmation -->
          <div v-if="currentStep === 3">
            <div class="text-center mb-6">
              <div
                class="size-16 rounded-2xl bg-woot-50 dark:bg-woot-900/20 flex items-center justify-center mx-auto mb-4"
              >
                <span class="i-lucide-file-check size-8 text-woot-500" />
              </div>
              <h3 class="text-lg font-semibold text-n-slate-12 mb-1">
                {{ t('SEGMENT_IMPORT.CONFIRM.READY') }}
              </h3>
            </div>

            <label
              class="mb-4 flex cursor-pointer items-start gap-2.5 rounded-xl border border-n-weak bg-n-solid-2 px-4 py-3"
            >
              <input
                v-model="avisarFluxos"
                type="checkbox"
                class="mt-0.5 accent-n-brand"
              />
              <span>
                <b class="block text-sm text-n-slate-12">
                  {{ t('SEGMENT_IMPORT.CONFIRM.NOTIFY_FLOWS') }}
                </b>
                <span class="text-xs text-n-slate-10">
                  {{ t('SEGMENT_IMPORT.CONFIRM.NOTIFY_FLOWS_HINT') }}
                </span>
              </span>
            </label>

            <!-- Dedup stats cards -->
            <div v-if="dedupStats" class="grid grid-cols-3 gap-3 mb-4">
              <div class="bg-n-slate-2 rounded-xl p-4 text-center">
                <div class="text-2xl font-bold font-headline text-n-slate-12">
                  {{ dedupStats.total }}
                </div>
                <div class="text-xs text-n-slate-10 mt-1">
                  {{ t('SEGMENT_IMPORT.CONFIRM.DETECTED') }}
                </div>
              </div>
              <div
                class="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 text-center"
              >
                <div
                  class="text-2xl font-bold font-headline text-green-600 dark:text-green-400"
                >
                  {{ dedupStats.new_contacts }}
                </div>
                <div class="text-xs text-green-600 dark:text-green-400 mt-1">
                  {{ t('SEGMENT_IMPORT.CONFIRM.NEW') }}
                </div>
              </div>
              <div
                class="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 text-center"
              >
                <div
                  class="text-2xl font-bold font-headline text-blue-600 dark:text-blue-400"
                >
                  {{ dedupStats.existing_contacts }}
                </div>
                <div class="text-xs text-blue-600 dark:text-blue-400 mt-1">
                  {{ t('SEGMENT_IMPORT.CONFIRM.EXISTING') }}
                </div>
              </div>
            </div>

            <div class="bg-n-slate-2 rounded-xl p-4 space-y-3">
              <div class="flex items-center justify-between">
                <span class="text-sm text-n-slate-10">
                  {{ t('SEGMENT_IMPORT.CONFIRM.FILE') }}
                </span>
                <span class="text-sm font-medium text-n-slate-12">
                  {{ fileName }}
                </span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-n-slate-10">
                  {{ t('SEGMENT_IMPORT.CONFIRM.MAPPED') }}
                </span>
                <span class="text-sm font-medium text-n-slate-12">
                  {{ mappedFieldCount }}
                </span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-sm text-n-slate-10">
                  {{ t('SEGMENT_IMPORT.CONFIRM.TARGET') }}
                </span>
                <span
                  class="text-sm font-medium text-woot-600 dark:text-woot-400"
                >
                  {{ segmentName || t('SEGMENT_IMPORT.CONFIRM.NONE') }}
                </span>
              </div>
            </div>

            <div
              class="flex items-start gap-2 mt-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400"
            >
              <span class="i-lucide-info size-4 shrink-0 mt-0.5" />
              <div class="text-xs space-y-1">
                <p>{{ t('SEGMENT_IMPORT.CONFIRM.INFO_DEDUP') }}</p>
                <p>{{ t('SEGMENT_IMPORT.CONFIRM.INFO_NOTIFY') }}</p>
              </div>
            </div>
          </div>

          <!-- STEP 4: Result -->
          <div v-if="currentStep === 4">
            <div class="text-center">
              <template v-if="importResult?.success">
                <div
                  class="size-16 rounded-2xl bg-green-50 dark:bg-green-900/20 flex items-center justify-center mx-auto mb-4"
                >
                  <span class="i-lucide-check-circle size-8 text-green-500" />
                </div>
                <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
                  {{ t('SEGMENT_IMPORT.RESULT.STARTED') }}
                </h3>
                <p class="text-sm text-n-slate-10 mb-2">
                  {{
                    t('SEGMENT_IMPORT.RESULT.PROCESSING', {
                      count: importResult.total,
                    })
                  }}
                </p>
                <p class="text-xs text-n-slate-9">
                  {{ t('SEGMENT_IMPORT.RESULT.SEGMENT_NOTE') }}
                </p>
              </template>
              <template v-else>
                <div
                  class="size-16 rounded-2xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-4"
                >
                  <span class="i-lucide-alert-circle size-8 text-red-500" />
                </div>
                <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
                  {{ t('SEGMENT_IMPORT.RESULT.ERROR_TITLE') }}
                </h3>
                <p class="text-sm text-red-500 mb-4">
                  {{
                    importResult?.error || t('SEGMENT_IMPORT.RESULT.UNKNOWN')
                  }}
                </p>
                <button
                  class="text-sm text-woot-500 hover:text-woot-600 font-medium"
                  @click="reset"
                >
                  {{ t('SEGMENT_IMPORT.RESULT.RETRY') }}
                </button>
              </template>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div
          class="flex items-center justify-between px-6 py-4 border-t border-n-weak shrink-0"
        >
          <button
            v-if="currentStep > 1 && currentStep < 4"
            class="text-sm text-n-slate-10 hover:text-n-slate-12"
            @click="currentStep--"
          >
            {{ t('SEGMENT_IMPORT.BACK') }}
          </button>
          <div v-else />

          <div class="flex items-center gap-3">
            <button
              v-if="currentStep === 4"
              class="px-4 py-2 text-sm font-medium rounded-lg bg-woot-500 text-white hover:bg-woot-600 transition-colors"
              @click="close"
            >
              {{ t('SEGMENT_IMPORT.RESULT.DONE') }}
            </button>
            <button
              v-if="currentStep === 2"
              class="px-4 py-2 text-sm font-medium rounded-lg bg-woot-500 text-white hover:bg-woot-600 transition-colors disabled:opacity-50"
              :disabled="!hasEmailOrPhone || isChecking"
              @click="goToConfirmation"
            >
              <span
                v-if="isChecking"
                class="i-lucide-loader-2 size-4 animate-spin inline-block mr-1 align-middle"
              />
              {{
                isChecking
                  ? t('SEGMENT_IMPORT.MAPPING.CHECKING')
                  : t('SEGMENT_IMPORT.MAPPING.CONTINUE')
              }}
            </button>
            <button
              v-if="currentStep === 3"
              class="px-4 py-2 text-sm font-medium rounded-lg bg-green-500 text-white hover:bg-green-600 transition-colors disabled:opacity-50"
              :disabled="isImporting"
              @click="startImport"
            >
              <span
                v-if="isImporting"
                class="i-lucide-loader-2 size-4 animate-spin inline-block mr-1 align-middle"
              />
              {{
                isImporting
                  ? t('SEGMENT_IMPORT.CONFIRM.IMPORTING')
                  : t('SEGMENT_IMPORT.CONFIRM.IMPORT_NOW')
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
